// JournalEntryDataGrid.jsx
import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo, useCallback, useRef } from 'react';
import {
    DataGrid,
    GridToolbar,
    GridRowModes,
    GridActionsCellItem,
    useGridApiRef,
    gridFilteredSortedRowIdsSelector,
} from '@mui/x-data-grid';
import {
    getGridStringOperatorsWithComparison,
    getGridNumericOperatorsWithNotLike,
    getGridDateOperatorsWithNotLike,
} from '../../../Utils/DataGridFilterOperators';
import { styled } from '@mui/material/styles';
import {
    IconButton,
    Checkbox,
    Button,
    Box,
    Typography,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormControlLabel,
    Autocomplete,
    CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { enUS, arSA, he } from 'date-fns/locale';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import NoDocumentsPdf from '../../../assests/pdf/NoDocument.pdf';
import editIcon from '../../../assests/images/Icons/editIcon.svg';
import deleteIcon from '../../../assests/images/Icons/deleteIcon.svg';
import saveIcon from '../../../assests/images/Icons/saveIcon.svg';
import cancelIcon from '../../../assests/images/Icons/cancelIcon.svg';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { TextField, Popper, Tooltip, Popover, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { FitColumnsButton, useFitTableColumns } from '../../../hooks/useFitTableColumns';
import { calculateVatAmount } from '../../../Utils/GetVatValue';
import AddAccountCardModal from '../../Widgets/AddAccountCardModal/AddAccountCardModal';
import { journalEntryTypeLabel } from '../../../assests/Constants/journalEntryTypes';
import AddJournalEntryModal from './AddJournalEntryModal.jsx';
import styles from './JournalEntryDataGrid.module.scss';

const STORAGE_KEY_JOURNAL_ENTRY_VIEW = 'journalEntryDataGrid_view';
const STORAGE_KEY_JOURNAL_ENTRY_COLUMN_WIDTHS = 'journalEntryDataGrid_columnWidths';
const STORAGE_KEY_JOURNAL_ENTRY_COLUMN_VISIBILITY = 'journalEntryDataGrid_columnVisibility';
const STORAGE_KEY_JOURNAL_ENTRY_COLUMN_VISIBILITY_CUSTOM = 'journalEntryDataGrid_columnVisibility_custom';
const TABLE_VIEWS = ['compact', 'standard', 'full', 'custom'];
const VIEW_INCLUDES_TIER = {
    compact: ['priority', 'compact'],
    standard: ['priority', 'compact', 'standard'],
    full: ['priority', 'compact', 'standard', 'full'],
    custom: ['priority', 'compact', 'standard', 'full'],
};

// Extended string filter operators: default + not like, !=, >=, <= (t = translation function)
// Numeric filter operators: default + contains, not like (so 'contains' is valid for numeric columns)
// Date filter operators: default + not like (not on / not equals)
// — All defined in Utils/DataGridFilterOperators.js

// Styled Popper for wider dropdowns
const StyledProjectsPopper = styled(Popper)({
    '& .MuiPaper-root': {
        borderRadius: '8px',
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        boxShadow: '0 12px 28px rgba(20, 30, 43, 0.16)',
        overflow: 'hidden',
    },
    '& .MuiAutocomplete-listbox': {
        padding: 0,
        textAlign: 'start',
    },
    '& .MuiAutocomplete-option': {
        justifyContent: 'flex-start',
        textAlign: 'start',
        fontSize: 'var(--xtram-text-sm, 13px)',
        fontWeight: 500,
        minHeight: 'var(--xtram-menu-item-height, 36px)',
        '&[aria-selected="true"]': {
            background: 'rgba(48, 79, 255, 0.1) !important',
            color: '#304fff',
            fontWeight: 700,
        },
        '&.Mui-focused, &:hover': {
            background: '#304fff !important',
            color: '#ffffff',
        },
    },
});

const TABLE_VIEW_MENU_PROPS = {
    PaperProps: {
        sx: {
            mt: 0.5,
            borderRadius: '8px',
            border: '1px solid #e5e7f6',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            minWidth: 'var(--xtram-menu-width)',
            '& .MuiList-root': { p: 0 },
            '& .MuiMenuItem-root': {
                minHeight: 'var(--xtram-menu-item-height)',
                px: 'var(--xtram-menu-item-pad-x)',
                py: 'var(--xtram-menu-item-pad-y)',
                fontSize: 'var(--xtram-text-sm)',
                fontWeight: 400,
                lineHeight: 1.35,
                color: '#696665',
                transition: 'background-color 0.2s ease, color 0.2s ease',
                '&:not(:last-of-type)': { borderBottom: '1px solid #f0f0f0' },
                '&:hover': { backgroundColor: '#304fff', color: '#ffffff' },
                '&.Mui-selected': {
                    backgroundColor: 'rgba(48, 79, 255, 0.1)',
                    color: '#304fff',
                    fontWeight: 500,
                },
                '&.Mui-selected:hover': { backgroundColor: '#304fff', color: '#ffffff' },
            },
        },
    },
};

const COLUMN_PANEL_PAPER_SX = {
    p: 0,
    mt: 0.5,
    maxHeight: 360,
    width: 'var(--xtram-menu-width)',
    minWidth: 'var(--xtram-menu-width)',
    maxWidth: 'var(--xtram-menu-width)',
    borderRadius: '8px',
    border: '1px solid #e5e7f6',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden',
};

const StyledPopper = styled(Popper)(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: '8px',
        background: '#FFF',
        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.15)',
        minWidth: '450px !important',
        maxWidth: '650px !important',
    },
    '& .MuiAutocomplete-listbox': {
        padding: theme.spacing(1),
        maxHeight: '400px',
    },
    '& .MuiAutocomplete-option': {
        borderRadius: '4px',
        padding: '12px 16px',
        fontSize: '0.875rem',
        margin: '2px 0',
        '&[aria-selected="true"]': {
            background: 'rgba(48, 79, 255, 0.12) !important',
            fontWeight: 600,
        },
        '&.Mui-focused': {
            background: 'rgba(48, 79, 255, 0.1) !important',
        },
        '&:hover': {
            background: '#304fff !important',
            color: '#ffffff',
        },
    },
}));

const StyledListItem = styled('li')({
    '&:hover': { backgroundColor: 'rgba(48, 79, 255, 0.08)', cursor: 'pointer' },
});

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    width: '100%',
    '& .MuiAutocomplete-root': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: "center"
    },
    '& *': {
        boxSizing: 'border-box !important',
    },
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#E3E7FE ',
        minHeight: '42px !important',
        maxHeight: '42px !important',
        lineHeight: '42px',
    },
    '& .MuiDataGrid-columnHeader': {
        backgroundColor: '#E3E7FE ',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 8px',
        minHeight: '42px !important',
        maxHeight: '42px !important',
        height: '42px !important',
        boxSizing: 'border-box',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        color: '#1222b3',
        fontFamily: 'Montserrat',
        fontSize: '13px',
        fontWeight: 600,
        lineHeight: 'normal',
    },
    '& .MuiDataGrid-sortIcon': {
        opacity: '0.8 !important',
        color: '#1222b3',
    },
    '& .MuiDataGrid-menuIconButton': {
        color: '#1222b3',
    },
    '& .MuiDataGrid-main, & .MuiDataGrid-overlayWrapper': {
        backgroundColor: '#ffffff',
    },
    '& .MuiDataGrid-virtualScroller': {
        backgroundColor: '#ffffff',
        overflowX: 'auto',
        scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
        scrollbarColor: '#d8daf2 transparent',
        borderRadius: 'inherit',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
        background: '#d8daf2',
        borderRadius: 'var(--xtram-radius-pill, 999px)',
    },
    '& .MuiDataGrid-scrollbar': {
        scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
        scrollbarColor: '#d8daf2 transparent',
        borderRadius: 'inherit',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar-thumb': {
        background: '#d8daf2',
        borderRadius: 'var(--xtram-radius-pill, 999px)',
    },
    '& .MuiDataGrid-scrollbar.MuiDataGrid-scrollbar--horizontal': {
        display: 'block',
        height: '6px',
    },
    '& .MuiDataGrid-cell': {
        borderTop: '1px solid rgba(191, 191, 193, 0.56)',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        lineHeight: 1.3,
        overflow: 'hidden',
        /* ~4 wrapped lines + padding — don't grow much beyond that */
        minHeight: '50px',
        maxHeight: '90px',
    },
    '& .MuiDataGrid-row': {
        minHeight: '50px',
        maxHeight: '90px !important',
    },
    '& .MuiDataGrid-cell:focus': {
        outline: 'none',
    },
    '& .MuiDataGrid-cellContent, & .MuiDataGrid-cell > span, & .MuiDataGrid-cell .description-cell': {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 4,
        overflow: 'hidden',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        maxWidth: '100%',
        lineHeight: 1.3,
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: 'rgba(227, 230, 255, 0.3)',
    },
    '& .disableUser': {
        pointerEvents: 'none',
        opacity: 0.5,
    },
    '& .MuiDataGrid-cell--editing': {
        padding: 0,
        backgroundColor: 'inherit',
    },
    '& .MuiDataGrid-row--editing .MuiDataGrid-cell': {
        borderRight: 'none',
    },
    '& .MuiDataGrid-row--editing .MuiDataGrid-cell:not(:last-child)': {
        borderRight: '8px solid transparent',
    },
    '& .MuiDataGrid-row.new-journal-entry-row': {
        backgroundColor: 'rgba(76, 175, 80, 0.08) !important',
        borderTop: '2px solid #4CAF50',
        borderBottom: '2px solid #4CAF50',
    },
    '& .MuiDataGrid-row.new-journal-entry-row .MuiDataGrid-cell': {
        padding: '8px 4px !important',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-row.new-journal-entry-row:hover': {
        backgroundColor: 'rgba(76, 175, 80, 0.12) !important',
    },
}));

const JournalEntryDataGrid = forwardRef((props, ref) => {
    const {
        t,
        client,
        data,
        isRange,
        isClosedReportingMonth,
        accountCardOptions,
        subAccountantOptions,
        creditAccountOptions,
        debitAccountOptions,
        sortCodeIdOptions,
        formatDateNew,
        formatMonthYearDate,
        beautifyNumber,
        Constants,
        handleDeleteClick,
        handleSave,
        handleCancel,
        direction,
        setEditedItem,
        editedItem,
        setEditedPopupItem,
        handleEdit,
        setCurrentRowData,
        setOpenInlineEditModal,
        handleSelectRows,
        addNewJournalEntry,
        openPdfInNewTab,
        setAddNewJournalEntry,
        handleOpenDialog,
        handleRightClickAction,
        complexDepositsChecked,
        setDebitCreditSum,
        setCreditSum,
        setDebitSum,
        showSearch = false,
        allowAllSelect,
        sortDate,
        startDate,
        endDate,
        isDepositTable = false,
        token,
        filteredAndSortedData = data,
        handleReopenEdit = handleEdit,
        fetchEnums = () => {},
        palCodes = [],
        palCode = '',
        setPalCode = () => {},
        setLoading = () => {},
        byProjectsFilterActive = false,
        onByProjectsFilterToggle = () => {},
        loadingSegmentations = false,
        projectAutocompleteOptions = [],
        selectedFromSegmentation = null,
        selectedToSegmentation = null,
        onFromSegmentationChange = () => {},
        onToSegmentationChange = () => {},
        onFilteredRowsChange,
    } = props;

    const sharedInputProps = {
        disableUnderline: true,
        className: "custom-input-box-sizing-toggle",
        sx: {
            height: "48px",
            padding: "0 10px",
            borderRadius: "8px",
            background: "#F5F5F6",
            border: "1px solid transparent",
            "&:focus-within": {
                border: "2px solid var(--Foundation-Blue-Normal, #304FFF)",
            },
        },
    };

    const sharedInputStyle = dir => ({
        style: { textAlign: dir === "ltr" ? "left" : "right" },
    });

    const { i18n } = useTranslation();
    const apiRef = useGridApiRef();
    const onFilteredRowsChangeRef = useRef(onFilteredRowsChange);
    onFilteredRowsChangeRef.current = onFilteredRowsChange;
    const [tableView, setTableView] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY_JOURNAL_ENTRY_VIEW);
            return TABLE_VIEWS.includes(saved) ? saved : 'standard';
        } catch {
            return 'standard';
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_JOURNAL_ENTRY_VIEW, tableView);
        } catch (_) {}
    }, [tableView]);
    const [columnWidths, setColumnWidths] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY_JOURNAL_ENTRY_COLUMN_WIDTHS);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            return typeof parsed === 'object' && parsed !== null ? parsed : {};
        } catch {
            return {};
        }
    });
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
        try {
            const savedView = localStorage.getItem(STORAGE_KEY_JOURNAL_ENTRY_VIEW);
            if (savedView === 'custom') {
                const rawCustom = localStorage.getItem(STORAGE_KEY_JOURNAL_ENTRY_COLUMN_VISIBILITY_CUSTOM);
                if (rawCustom) {
                    const parsed = JSON.parse(rawCustom);
                    if (typeof parsed === 'object' && parsed !== null) return { actions: true, ...parsed };
                }
            }
            const raw = localStorage.getItem(STORAGE_KEY_JOURNAL_ENTRY_COLUMN_VISIBILITY);
            if (!raw) return { actions: true };
            const parsed = JSON.parse(raw);
            return typeof parsed === 'object' && parsed !== null ? { actions: true, ...parsed } : { actions: true };
        } catch {
            return { actions: true };
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_JOURNAL_ENTRY_COLUMN_VISIBILITY, JSON.stringify(columnVisibilityModel));
            if (tableView === 'custom') {
                localStorage.setItem(STORAGE_KEY_JOURNAL_ENTRY_COLUMN_VISIBILITY_CUSTOM, JSON.stringify(columnVisibilityModel));
            }
        } catch (_) {}
    }, [columnVisibilityModel, tableView]);
    const handleColumnVisibilityModelChange = (newModel) => {
        setColumnVisibilityModel(newModel);
    };
    const [columnsPanelAnchor, setColumnsPanelAnchor] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [rows, setRows] = useState(data);
    const [rowModesModel, setRowModesModel] = useState({});
    const [selectedTotals, setSelectedTotals] = useState({
        amount: 0,
        vatAmount: 0,
        netAmount: 0
    });

    // Local state to control inline add mode (independent from parent)
    const [isInlineAddMode, setIsInlineAddMode] = useState(false);

    // State for new journal entry inline editing
    const [newRowData, setNewRowData] = useState({
        id: 'NEW_ROW',
        accountCodeName: '',
        accountCaseId: '',
        accountCodeId: '',
        reference: '',
        reference2: '',
        description: '',
        uploadDate: new Date(), // Current date/time
        documentDate: new Date(),
        amount: '',
        vat: 0,
        creditAccount: '',
        debitAccount: '',
        sortCodeId: '3',
        processed: false,
        confirmationNumber: '',
            journalEntryType: 0,
            documents: [],
            addAsProcessed: true,
        });

    // State for tooltips
    const [focusedField, setFocusedField] = useState(null);
    const [tooltipValue, setTooltipValue] = useState('');
    const [tooltipAnchor, setTooltipAnchor] = useState(null);

    // Autocomplete list open state (Enter opens list instead of submitting form)
    const [openSortCodeId, setOpenSortCodeId] = useState(false);
    const [openDebitAccount, setOpenDebitAccount] = useState(false);
    const [openCreditAccount, setOpenCreditAccount] = useState(false);

    // AddAccountCardModal state (for inline add form)
    const [editedAddAccountCard, setEditedAddAccountCard] = useState(false);
    const [newAccountCard, setNewAccountCard] = useState({
        accountCodeName: '',
        accountCodeId: '',
        accountCaseId: '',
    });
    const [isEditAccountCard, setIsEditAccountCard] = useState(false);
    const [addAccountCardType, setAddAccountCardType] = useState('creditAccount');
    const [cardIdOption, setCardIdOption] = useState(0);
    const [searchTermDebit, setSearchTermDebit] = useState('');
    const [searchTermCredit, setSearchTermCredit] = useState('');

    // Ref to focus Code ID (Sort Code) after saving a new entry for fast repeat entry
    const sortCodeInputRef = React.useRef(null);
    // Modal applies newly created account cards to the correct draft row
    const applyAccountCardToDraftRef = React.useRef(null);

    // Format date as dd/mm/yyyy for new-entry form tooltips
    const formatTooltipDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        return `${day}/${month}/${d.getFullYear()}`;
    };

    // New-entry form tooltip style (dark blue, white text – not grey)
    const newEntryTooltipSx = {
        '& .MuiTooltip-tooltip': {
            maxWidth: 420,
            fontSize: '1.25rem',
            fontWeight: 700,
            backgroundColor: '#1a237e',
            color: '#fff',
        },
        '& .MuiTooltip-arrow': { color: '#1a237e' },
    };

    // Enhanced debit/credit options with "Add new account card" as first option
    const enhancedDebitAccountOptions = useMemo(() => {
        const addNewOption = {
            cardId: 'ADD_NEW_ACCOUNT',
            name: t('AddAccountCard'),
            isAddNew: true,
        };
        return [addNewOption, ...(debitAccountOptions || [])];
    }, [debitAccountOptions, t]);

    const enhancedCreditAccountOptions = useMemo(() => {
        const addNewOption = {
            cardId: 'ADD_NEW_ACCOUNT',
            name: t('AddAccountCard'),
            isAddNew: true,
        };
        return [addNewOption, ...(creditAccountOptions || [])];
    }, [creditAccountOptions, t]);

    useEffect(() => {
        if (accountCardOptions?.length > 0) {
            const maxCardId = Math.max(
                ...accountCardOptions.map((opt) => parseInt(opt.cardId, 10) || 0)
            );
            setCardIdOption(maxCardId + 1);
        } else {
            setCardIdOption(1);
        }
    }, [accountCardOptions]);

    const handleNoOptionCreditDebitClick = (searchTerm, fieldName) => {
        setEditedAddAccountCard(true);
        setIsEditAccountCard(false);
        setAddAccountCardType(fieldName);
        setNewAccountCard({
            accountCodeName: searchTerm || '',
            accountCodeId: cardIdOption.toString(),
            accountCaseId: '',
        });
    };

    const handleCloseAddAccountCard = () => {
        setEditedAddAccountCard(false);
    };

    const handleChangeAfterAdd = (newlyAddedCardId, type, addedAccountCaseId) => {
        if (typeof applyAccountCardToDraftRef.current === 'function') {
            applyAccountCardToDraftRef.current(newlyAddedCardId, type, addedAccountCaseId);
            return;
        }
        setNewRowData((prev) => ({
            ...prev,
            [type]: newlyAddedCardId,
            ...(addedAccountCaseId != null && addedAccountCaseId !== ''
                ? { accountCaseId: addedAccountCaseId }
                : {}),
        }));
    };

    const deselectRows = (ids) => {

        setSelectedRows((prevSelectedRows) => prevSelectedRows.filter(rowId => !ids.includes(rowId)));
    };
    useImperativeHandle(ref, () => ({
        deselectRows
    }));

    const [searchQuery, setSearchQuery] = useState('');
    const filteredRows = React.useMemo(() => {
        if (!searchQuery) return rows;

        const q = searchQuery.toLowerCase();
        return rows.filter(r => {
            const creditLabel = creditAccountOptions?.find(opt => opt.cardId === r.creditAccount)?.name ?? '';
            const debitLabel = debitAccountOptions?.find(opt => opt.cardId === r.debitAccount)?.name ?? '';
            const sortCodeLabel = sortCodeIdOptions?.find(opt => opt.sortCodeId === r.sortCodeId)?.sortCodeName ?? '';
            return [
                r.reference,
                r.description,
                r.accountCaseId,
                r.creditAccount,
                creditLabel,
                r.debitAccount,
                debitLabel,
                r.sortCodeId,
                sortCodeLabel,
                r.confirmationNumber,
                r.uploadNumber,
                r.amount?.toString(),
            ]
                .filter(Boolean)
                .some(f => f.toString().toLowerCase().includes(q));
        });
    }, [rows, searchQuery, creditAccountOptions, debitAccountOptions, sortCodeIdOptions]);

    const emitFilteredRows = useCallback(() => {
        const cb = onFilteredRowsChangeRef.current;
        if (!cb) return;
        const sourceRows = (filteredRows || []).filter((r) => r.id !== 'NEW_ROW');
        try {
            const ids = gridFilteredSortedRowIdsSelector(apiRef);
            if (!Array.isArray(ids)) {
                cb(sourceRows);
                return;
            }
            // Build a lookup so we can return rows in the grid's filtered + sorted order,
            // ensuring EditModal next/previous follows the on-screen order.
            const rowById = new Map();
            sourceRows.forEach((r) => {
                rowById.set(r.id, r);
                rowById.set(String(r.id), r);
            });
            const orderedRows = ids
                .map((id) => rowById.get(id) ?? rowById.get(String(id)))
                .filter((r) => r && r.id !== 'NEW_ROW');
            cb(orderedRows);
        } catch {
            cb(sourceRows);
        }
    }, [filteredRows, apiRef]);

    useEffect(() => {
        const frame = requestAnimationFrame(emitFilteredRows);
        return () => cancelAnimationFrame(frame);
    }, [emitFilteredRows, filteredRows, tableView]);

    const getLocale = () => {
        switch (i18n.language) {
            case 'ar':
                return arSA;
            case 'he':
                return he;
            default:
                return enUS;
        }
    };

    const handleCellDoubleClick = (params) => {
        const row = params?.row || {};

        // Don't allow double-click editing for the new row
        if (row.id === 'NEW_ROW') return;

        const rowIndex = data.findIndex((r) => r.id === row.id); // Find the index in the original data array
        handleEdit(row, rowIndex)
    };

    // Get custom class name for rows
    const getRowClassName = (params) => {
        if (params.row.id === 'NEW_ROW') {
            return 'new-journal-entry-row';
        }
        return '';
    };

    // Helper function to parse amount values (handles both numbers and formatted strings with parentheses)
    const parseAmountValue = (value) => {
        if (value === null || value === undefined) return 0;

        // If it's already a number, return it
        if (typeof value === 'number') return value;

        // If it's a string, parse it (handling parentheses notation)
        if (typeof value === 'string') {
            const strValue = value.trim();
            // Check if it's wrapped in parentheses (negative number format)
            if (strValue.startsWith('(') && strValue.endsWith(')')) {
                const cleanValue = strValue.slice(1, -1).replace(/,/g, '');
                return -parseFloat(cleanValue) || 0;
            }
            // Remove commas and parse normally
            const cleanValue = strValue.replace(/,/g, '');
            return parseFloat(cleanValue) || 0;
        }

        return parseFloat(value) || 0;
    };

    const handleSelectionModelChange = (newSelection) => {
        setSelectedRows(newSelection);
        handleSelectRows(newSelection);

        // Calculate totals for selected rows
        if (newSelection.length > 0) {
            const selectedRowsData = data.filter((row) =>
                newSelection.includes(row.id)
            );

            let totalAmount = 0;
            let totalVatAmount = 0;
            let totalNetAmount = 0;

            selectedRowsData.forEach((row) => {
                const amount = parseAmountValue(row.amount);
                const vatAmount = parseAmountValue(row.vatAmount);
                const netAmount = parseAmountValue(row.netAmount);

                totalAmount += amount;
                totalVatAmount += vatAmount;
                totalNetAmount += netAmount;
            });

            // Round to two decimal places
            setSelectedTotals({
                amount: Math.round(totalAmount * 100) / 100,
                vatAmount: Math.round(totalVatAmount * 100) / 100,
                netAmount: Math.round(totalNetAmount * 100) / 100
            });
        } else {
            setSelectedTotals({
                amount: 0,
                vatAmount: 0,
                netAmount: 0
            });
        }

        if (complexDepositsChecked) {
            const selectedRowsData = data.filter((row) =>
                newSelection.includes(row.id)
            );

            let debitTotal = 0;
            let creditTotal = 0;
            selectedRowsData.forEach((row) => {
                const amount = parseAmountValue(row.amount);

                if (row.debitAccount) {
                    debitTotal += amount;
                } else if (row.creditAccount) {
                    creditTotal += amount;
                }
            });

            const roundedDebit = Math.round(debitTotal * 100) / 100;
            const roundedCredit = Math.round(creditTotal * 100) / 100;
            const sum = roundedDebit - roundedCredit;
            const finalSum = Math.abs(sum) < 0.1 ? 0 : sum;
            setDebitCreditSum(finalSum);
            if (setCreditSum) setCreditSum(roundedCredit);
            if (setDebitSum) setDebitSum(roundedDebit);
        } else {
            setDebitCreditSum(0);
            if (setCreditSum) setCreditSum(0);
            if (setDebitSum) setDebitSum(0);
        }
    };

    const isRowSelectable = (params) => {
        const { row } = params;

        // Don't allow selecting the new row
        if (row.id === 'NEW_ROW') return false;

        /* 2.  Master switch – if ON, everything above is enough             */
        if (allowAllSelect) return true;
        if (complexDepositsChecked) {
            // Only allow selection of rows where debit or credit is present, but not both
            const hasDebit = !!row.debitAccount;
            const hasCredit = !!row.creditAccount;
            return (hasDebit && !hasCredit) || (!hasDebit && hasCredit);
        } else {
            return client?.accountManagement === Constants.AccountManagementOptions[0].name
                ? row.sortCodeId && row.creditAccount && row.debitAccount
                : row.sortCodeId;
        }
    };

    // Sync parent prop with local state when parent opens add mode
    useEffect(() => {
        if (addNewJournalEntry && !isInlineAddMode) {
            setIsInlineAddMode(true);
        }
    }, [addNewJournalEntry]);

    // Show/hide Actions column based on isInlineAddMode
    useEffect(() => {
        setColumnVisibilityModel(prev => ({
            ...prev,
            actions: isInlineAddMode // true = hidden, false = visible
        }));
    }, [isInlineAddMode]);

    useEffect(() => {
        if (data) {
            setRows(data);
        }
    }, [data]);

    // Handle field changes for new row
    const handleNewRowChange = (field, value) => {
        setNewRowData(prev => {
            const updated = { ...prev, [field]: value };

            // Auto-calculate VAT amount and net amount when amount or vat changes
            if (field === 'amount' || field === 'vat') {
                const amount = field === 'amount' ? parseFloat(value) || 0 : parseFloat(prev.amount) || 0;
                const vat = field === 'vat' ? value : prev.vat;
                const documentDate = prev.documentDate || new Date();

                updated.vatAmount = calculateVatAmount(amount, vat, documentDate);
                updated.netAmount = amount - updated.vatAmount;
            }

            // Handle sort code change - auto-populate credit/debit accounts
            if (field === 'sortCodeId') {
                const sortCode = sortCodeIdOptions.find(sc => sc.sortCodeId === value);
                if (sortCode) {
                    updated.vat = sortCode.vat ?? 100;
                    if (sortCode.creditAccount) updated.creditAccount = sortCode.creditAccount;
                    if (sortCode.debitAccount) updated.debitAccount = sortCode.debitAccount;
                }
            }

            return updated;
        });
    };

    // Save one or many draft rows from AddJournalEntryModal (Save button only).
    const handleSaveNewRow = async (rowsInput) => {
        const list = Array.isArray(rowsInput)
            ? rowsInput
            : (rowsInput ? [rowsInput] : (newRowData?.amount ? [newRowData] : []));

        for (const row of list) {
            if (row.amount == null || row.amount === '') {
                continue;
            }

            const dataToSave = {
                ...row,
                registrationMonthDate: sortDate ? new Date(sortDate).toISOString() : new Date().toISOString(),
                reportingMonthDate: sortDate ? new Date(sortDate).toISOString() : new Date().toISOString(),
                uploadDate: row.uploadDate ? new Date(row.uploadDate).toISOString() : new Date().toISOString(),
                documentDate: row.documentDate ? new Date(row.documentDate).toISOString() : new Date().toISOString(),
            };

            delete dataToSave.id;

            await handleSave(dataToSave);
        }

        // Reset local template (modal owns visible drafts and resets itself after save)
        setNewRowData({
            id: 'NEW_ROW',
            accountCodeName: '',
            accountCaseId: '',
            accountCodeId: '',
            reference: '',
            reference2: '',
            description: '',
            uploadDate: new Date(),
            documentDate: new Date(),
            amount: '',
            vat: 0,
            vatAmount: 0,
            netAmount: 0,
            creditAccount: '',
            debitAccount: '',
            sortCodeId: '3',
            processed: false,
            confirmationNumber: '',
            journalEntryType: 0,
            documents: [],
            addAsProcessed: true,
        });
    };

    // Escape closes the add modal. Enter navigation/save is handled inside AddJournalEntryModal.
    useEffect(() => {
        if (!isInlineAddMode) return undefined;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                handleCancelNewRow();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isInlineAddMode]);

    // Handle cancel new row
    const handleCancelNewRow = () => {
        // Reset new row data with current date/time
        setNewRowData({
            id: 'NEW_ROW',
            accountCodeName: '',
            accountCaseId: '',
            accountCodeId: '',
            reference: '',
            reference2: '',
            description: '',
            uploadDate: new Date(), // Always set to current date/time
            documentDate: new Date(),
            amount: '',
            vat: 0,
            vatAmount: 0,
            netAmount: 0,
            creditAccount: '',
            debitAccount: '',
            sortCodeId: '3',
            processed: false,
            confirmationNumber: '',
            journalEntryType: 0,
            documents: [],
            addAsProcessed: true,
        });

        // Close the new row - both local and parent state
        setIsInlineAddMode(false);
        setAddNewJournalEntry(false);
    };



    const getAlignment = () => {
        return i18n.language === 'en' ? 'center' : 'center';
    };

    // Memoize filter operators so column defs are stable (avoids "once show once no" when using valueGetter)
    const stringFilterOperators = useMemo(() => getGridStringOperatorsWithComparison(t), [t]);
    const numericFilterOperators = useMemo(() => getGridNumericOperatorsWithNotLike(t), [t]);
    const dateFilterOperators = useMemo(() => getGridDateOperatorsWithNotLike(t), [t]);

    const columns = [

        // Actions Column (for new row only)
        // {
        //     field: 'actions',
        //     type: 'actions',
        //     headerName: t('Actions'),
        //     headerAlign: getAlignment(),
        //     align: getAlignment(),
        //     width: 80,
        //     viewTier: 'priority',
        //     hide: true, // Hidden by default, shown when NEW_ROW is added
        //     getActions: (params) => {
        //         if (params.row.id === 'NEW_ROW') {
        //             const isSaveDisabled = !newRowData.amount;
        //             return [
        //                 <IconButton
        //                     onClick={handleSaveNewRow}
        //                     disabled={isSaveDisabled}
        //                     tabIndex={0}
        //                     onKeyDown={(e) => {
        //                         if (e.key === 'Enter') {
        //                             e.preventDefault();
        //                             handleSaveNewRow();
        //                         }
        //                     }}
        //                     sx={{
        //                         color: isSaveDisabled ? '#bdbdbd' : '#4CAF50',
        //                         '&:focus': {
        //                             outline: '2px solid #4CAF50',
        //                             outlineOffset: '2px',
        //                         },
        //                         '&:hover': {
        //                             backgroundColor: isSaveDisabled ? 'transparent' : 'rgba(76, 175, 80, 0.1)',
        //                         }
        //                     }}
        //                 >
        //                     <SaveIcon sx={{ fontSize: '1.2rem' }} />
        //                 </IconButton>,
        //                 <IconButton
        //                     onClick={handleCancelNewRow}
        //                     tabIndex={0}
        //                     onKeyDown={(e) => {
        //                         if (e.key === 'Enter') {
        //                             e.preventDefault();
        //                             handleCancelNewRow();
        //                         }
        //                     }}
        //                     sx={{
        //                         color: '#F44336',
        //                         '&:focus': {
        //                             outline: '2px solid #F44336',
        //                             outlineOffset: '2px',
        //                         },
        //                         '&:hover': {
        //                             backgroundColor: 'rgba(244, 67, 54, 0.1)',
        //                         }
        //                     }}
        //                 >
        //                     <CancelIcon sx={{ fontSize: '1.2rem' }} />
        //                 </IconButton>,
        //             ];
        //         }
        //         return [];
        //     },
        // },
        {
            field: 'document',
            headerName: t('Document'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'priority',
            renderCell: (params) => {
                // New row - show file upload
                if (params.row.id === 'NEW_ROW') {
                    return (
                        <Button
                            variant="contained"
                            size="small"
                            component="label"
                            sx={{
                                height: '36px',
                                fontSize: '0.75rem',
                                minWidth: 'auto',
                                padding: '0 12px',
                                backgroundColor: newRowData.documents?.length > 0 ? '#4CAF50' : '#304FFF',
                                color: '#fff',
                                '&:hover': {
                                    backgroundColor: newRowData.documents?.length > 0 ? '#45a049' : '#2441e7',
                                },
                                textTransform: 'none',
                                fontWeight: 500,
                            }}
                        >
                            {newRowData.documents?.length > 0
                                ? `${newRowData.documents.length} ${t('Files')}`
                                : t('UploadFile')}
                            <input
                                type="file"
                                hidden
                                multiple
                                onChange={(e) => handleNewRowChange('documents', Array.from(e.target.files))}
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            />
                        </Button>
                    );
                }

                // Existing row - show document preview
                const docUrl = params.row.documentUrl || NoDocumentsPdf;

                return (
                    <div
                        className="iframe-container"
                        onClick={() => params.row.documentUrl && openPdfInNewTab(params.row.documentUrl)}
                        style={{ cursor: params.row.documentUrl ? 'pointer' : 'default' }}
                    >
                        <iframe
                            className="zoom-iframe"
                            src={`${docUrl}#page=1`}
                            width="100px"
                            height="50px"
                            title={params.row.title || 'No Title'}
                            style={{ pointerEvents: 'none', border: 'none' }}
                        />
                    </div>
                );
            },
        },
        // ProcessedBy column removed as requested
        {
            field: 'isLocked',
            headerName: t('Status'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'compact',
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    return <span>-</span>;
                }
                const isLocked = params.row.isLocked;
                return (
                    <div className={isLocked ? 'status-permanent' : 'status-temporary'}>
                        {isLocked ? t('Permanent') : t('Temporary')}
                    </div>
                );
            }
        },
        {
            field: 'journalEntryType',
            headerName: t('JournalEntryType'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'standard',
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    return <span>-</span>;
                }
                const journalEntryType = params.row.journalEntryType ?? params.row.JournalEntryType ?? 0;
                return (
                    <div>
                        {journalEntryTypeLabel(journalEntryType, t)}
                    </div>
                );
            }
        },
        {
            field: 'processed',
            headerName: t('DocumentProcessed'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'compact',
            type: 'singleSelect',
            valueOptions: [
                { value: 'processed', label: t('processed') },
                { value: 'unprocessed', label: t('unprocessed') },
            ],
            valueGetter: (value, row) => {
                if (row?.id === 'NEW_ROW') return '';
                const isUnprocessed = client?.documentIds?.journalEntries?.includes(row?.id);
                return isUnprocessed ? 'unprocessed' : 'processed';
            },
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    return <span>-</span>;
                }

                const isIdInChecks =
                    client?.documentIds?.journalEntries?.includes(params.row.id);

                const handleRightClick = (event) => {
                    event.preventDefault();
                    const status = isIdInChecks ? 'done' : 'notDone';
                    const id = params.row.id;
                    handleRightClickAction(id, status, params.row);
                };

                return (
                    <p
                        onContextMenu={handleRightClick}
                        className={isIdInChecks ? 'unprocessed' : 'processed'}
                        style={{
                            cursor: 'context-menu',
                        }}
                    >
                        {isIdInChecks ? t('unprocessed') : t('processed')}
                    </p>
                );
            },
            sortComparator: (v1, v2) => {
                if (v1 === v2) return 0;
                return v1 === 'unprocessed' ? 1 : -1;
            },
        },
        {
            field: 'createdBy',
            headerName: t('DocumentCreatedBy'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'standard',
            sortable: true,
            valueGetter: (value, row) => {
                if (row?.id === 'NEW_ROW') return '';
                const uid = row?.createdByUserId;
                const utype = row?.createdByUserType;
                if (!uid && !utype) return '';
                const found = subAccountantOptions?.find(option => option.value === uid);
                if (found?.label) return found.label;
                if (utype === 'Client') return t('Client');
                return uid && utype ? `${uid} (${utype})` : (uid || utype);
            },
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') return <span>-</span>;
                const uid = params.row.createdByUserId;
                const utype = params.row.createdByUserType;
                if (!uid && !utype) return '—';
                const found = subAccountantOptions?.find(option => option.value === uid);
                if (found?.label) return found.label;
                if (utype === 'Client') return t('Client');
                return uid && utype ? `${uid} (${utype})` : (uid || utype);
            },
        },
        {
            field: 'accountCaseId',
            headerName: t('CompanyId'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: false,
            flex: 1,
            viewTier: 'standard',
            filterOperators: stringFilterOperators,
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    return (
                        <Tooltip
                            title={newRowData.accountCaseId || t('CompanyId')}
                            open={focusedField === 'accountCaseId'}
                            placement="top"
                            arrow
                            PopperProps={{
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        backgroundColor: '#304FFF',
                                        fontSize: '1rem',
                                        padding: '10px 16px',
                                        maxWidth: '400px',
                                    },
                                    '& .MuiTooltip-arrow': {
                                        color: '#304FFF',
                                    }
                                }
                            }}
                        >
                            <TextField
                                size="small"
                                value={newRowData.accountCaseId || ''}
                                onChange={(e) => handleNewRowChange('accountCaseId', e.target.value)}
                                onFocus={() => setFocusedField('accountCaseId')}
                                onBlur={() => setFocusedField(null)}
                                placeholder={t('CompanyId')}
                                fullWidth
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: '40px',
                                        fontSize: '0.875rem',
                                        backgroundColor: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#f8f8f8',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: '#fff',
                                            boxShadow: '0 0 0 2px rgba(48, 79, 255, 0.2)',
                                        }
                                    }
                                }}
                            />
                        </Tooltip>
                    );
                }
                return params.value || '-';
            },
        },
        {
            field: 'reference2',
            headerName: t('Reference2'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: false,
            flex: 1,
            viewTier: 'full',
            filterOperators: stringFilterOperators,
            sortComparator: (v1, v2) => {
                const num1 = parseFloat(v1) || 0;
                const num2 = parseFloat(v2) || 0;
                return num1 - num2;
            },
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    return (
                        <Tooltip
                            title={newRowData.reference2 || t('Reference2')}
                            open={focusedField === 'reference2'}
                            placement="top"
                            arrow
                            PopperProps={{
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        backgroundColor: '#304FFF',
                                        fontSize: '1rem',
                                        padding: '10px 16px',
                                        maxWidth: '400px',
                                    },
                                    '& .MuiTooltip-arrow': {
                                        color: '#304FFF',
                                    }
                                }
                            }}
                        >
                            <TextField
                                size="small"
                                value={newRowData.reference2 || ''}
                                onChange={(e) => handleNewRowChange('reference2', e.target.value)}
                                onFocus={() => setFocusedField('reference2')}
                                onBlur={() => setFocusedField(null)}
                                placeholder={t('Reference2')}
                                fullWidth
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: '40px',
                                        fontSize: '0.875rem',
                                        backgroundColor: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#f8f8f8',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: '#fff',
                                            boxShadow: '0 0 0 2px rgba(48, 79, 255, 0.2)',
                                        }
                                    }
                                }}
                            />
                        </Tooltip>
                    );
                }
                return params.value || '-';
            },
        },
        {
            field: 'reference',
            headerName: t('Reference'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: false,
            flex: 1,
            viewTier: 'compact',
            filterOperators: stringFilterOperators,
            sortComparator: (v1, v2) => {
                const num1 = parseFloat(v1) || 0;
                const num2 = parseFloat(v2) || 0;
                return num1 - num2;
            },
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    return (
                        <Tooltip
                            title={newRowData.reference || t('Reference')}
                            open={focusedField === 'reference'}
                            placement="top"
                            arrow
                            PopperProps={{
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        backgroundColor: '#304FFF',
                                        fontSize: '1rem',
                                        padding: '10px 16px',
                                        maxWidth: '400px',
                                    },
                                    '& .MuiTooltip-arrow': {
                                        color: '#304FFF',
                                    }
                                }
                            }}
                        >
                            <TextField
                                size="small"
                                value={newRowData.reference || ''}
                                onChange={(e) => handleNewRowChange('reference', e.target.value)}
                                onFocus={() => setFocusedField('reference')}
                                onBlur={() => setFocusedField(null)}
                                placeholder={t('Reference')}
                                fullWidth
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: '40px',
                                        fontSize: '0.875rem',
                                        backgroundColor: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#f8f8f8',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: '#fff',
                                            boxShadow: '0 0 0 2px rgba(48, 79, 255, 0.2)',
                                        }
                                    }
                                }}
                            />
                        </Tooltip>
                    );
                }
                return params.value || '-';
            },
        },
        {
            field: 'confirmationNumber',
            headerName: t('ConfirmationNumber'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: false,
            flex: 1,
            viewTier: 'standard',
            filterOperators: stringFilterOperators,
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    return (
                        <Tooltip
                            title={newRowData.confirmationNumber || t('ConfirmationNumber')}
                            open={focusedField === 'confirmationNumber'}
                            placement="top"
                            arrow
                            PopperProps={{
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        backgroundColor: '#304FFF',
                                        fontSize: '1rem',
                                        padding: '10px 16px',
                                        maxWidth: '400px',
                                    },
                                    '& .MuiTooltip-arrow': {
                                        color: '#304FFF',
                                    }
                                }
                            }}
                        >
                            <TextField
                                size="small"
                                value={newRowData.confirmationNumber || ''}
                                onChange={(e) => handleNewRowChange('confirmationNumber', e.target.value)}
                                onFocus={() => setFocusedField('confirmationNumber')}
                                onBlur={() => setFocusedField(null)}
                                placeholder={t('ConfirmationNumber')}
                                fullWidth
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: '40px',
                                        fontSize: '0.875rem',
                                        backgroundColor: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#f8f8f8',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: '#fff',
                                            boxShadow: '0 0 0 2px rgba(48, 79, 255, 0.2)',
                                        }
                                    }
                                }}
                            />
                        </Tooltip>
                    );
                }
                return params.value || '-';
            },
        },
        {
            field: 'description',
            headerName: t('Description'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            editable: false,
            flex: 1,
            viewTier: 'priority',
            filterOperators: stringFilterOperators,
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    return (
                        <Tooltip
                            title={newRowData.description || t('Description')}
                            open={focusedField === 'description'}
                            placement="top"
                            arrow
                            PopperProps={{
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        backgroundColor: '#304FFF',
                                        fontSize: '1rem',
                                        padding: '10px 16px',
                                        maxWidth: '400px',
                                    },
                                    '& .MuiTooltip-arrow': {
                                        color: '#304FFF',
                                    }
                                }
                            }}
                        >
                            <TextField
                                size="small"
                                value={newRowData.description || ''}
                                onChange={(e) => handleNewRowChange('description', e.target.value)}
                                onFocus={() => setFocusedField('description')}
                                onBlur={() => setFocusedField(null)}
                                placeholder={t('Description')}
                                fullWidth
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: '40px',
                                        fontSize: '0.875rem',
                                        backgroundColor: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#f8f8f8',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: '#fff',
                                            boxShadow: '0 0 0 2px rgba(48, 79, 255, 0.2)',
                                        }
                                    }
                                }}
                            />
                        </Tooltip>
                    );
                }

                const descriptionText = params.value || '';
                // Flags
                const isDuplicateFrom = descriptionText.includes('שוכפל מ');
                const isDuplicate = descriptionText.includes('כפולה ל מסמך שמספרו') || descriptionText.includes('מסמך כפול למספר');

                // Build the class string
                const cellClass =
                    isDuplicateFrom ? 'duplicate-from-cell'
                        : isDuplicate ? 'flickering-cell'
                            : '';

                return (
                    <div
                        className={`description-cell ${cellClass}`}
                        title={descriptionText}
                    >
                        {descriptionText}
                    </div>
                );
            },
        },
        {
            field: 'reportingMonthDate',
            headerName: t('MonthReportDate'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            width: 120,
            viewTier: 'full',
            filterOperators: dateFilterOperators,
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1);
                const date2 = new Date(v2);
                return date1 - date2;
            },
            valueGetter: (value) => {
                if (!value) return null;
                const d = new Date(value);
                return isNaN(d.getTime()) ? null : d;
            },
            valueFormatter: (params) => formatMonthYearDate(params),
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    return formatMonthYearDate(newRowData.documentDate);
                }
                return formatMonthYearDate(params.value);
            },
        },
        {
            field: 'registrationMonthDate',
            headerName: t('MonthRegisterDate'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            width: 120,
            viewTier: 'full',
            filterOperators: dateFilterOperators,
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1);
                const date2 = new Date(v2);
                return date1 - date2;
            },
            valueGetter: (value) => {
                if (!value) return null;
                const d = new Date(value);
                return isNaN(d.getTime()) ? null : d;
            },
            valueFormatter: (params) => formatMonthYearDate(params),
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    return formatMonthYearDate(newRowData.documentDate);
                }
                return formatMonthYearDate(params.value);
            },
        },
        {
            field: 'uploadDate',
            headerName: t('RegistrationDate'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            type: 'date',
            viewTier: 'standard',
            flex: 1,
            filterOperators: dateFilterOperators,
            valueFormatter: (params) => formatDateNew(params),
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    const dateValue = newRowData.uploadDate
                        ? new Date(newRowData.uploadDate).toLocaleDateString()
                        : t('RegistrationDate');

                    return (
                        <Tooltip
                            title={dateValue}
                            open={focusedField === 'uploadDate'}
                            placement="top"
                            arrow
                            PopperProps={{
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        backgroundColor: '#304FFF',
                                        fontSize: '1rem',
                                        padding: '10px 16px',
                                        maxWidth: '400px',
                                    },
                                    '& .MuiTooltip-arrow': {
                                        color: '#304FFF',
                                    }
                                }
                            }}
                        >
                            <div>
                                <DatePicker
                                    value={newRowData.uploadDate}
                                    onChange={(date) => handleNewRowChange('uploadDate', date)}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            placeholder: t('RegistrationDate'),
                                            onFocus: () => setFocusedField('uploadDate'),
                                            onBlur: () => setFocusedField(null),
                                            sx: {
                                                '& .MuiInputBase-root': {
                                                    height: '40px',
                                                    fontSize: '0.875rem',
                                                    backgroundColor: '#fff',
                                                    '&:hover': {
                                                        backgroundColor: '#f8f8f8',
                                                    },
                                                    '&.Mui-focused': {
                                                        backgroundColor: '#fff',
                                                        boxShadow: '0 0 0 2px rgba(48, 79, 255, 0.2)',
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </Tooltip>
                    );
                }
                return formatDateNew(params.value) || '-';
            },
        },
        {
            field: 'documentDate',
            headerName: t('Document Date'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            editable: true,
            type: 'date',
            viewTier: 'compact',
            filterOperators: dateFilterOperators,
            valueFormatter: (params) => formatDateNew(params),
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    const dateValue = newRowData.documentDate
                        ? new Date(newRowData.documentDate).toLocaleDateString()
                        : t('Document Date');

                    return (
                        <Tooltip
                            title={dateValue}
                            open={focusedField === 'documentDate'}
                            placement="top"
                            arrow
                            PopperProps={{
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        backgroundColor: '#304FFF',
                                        fontSize: '1rem',
                                        padding: '10px 16px',
                                        maxWidth: '400px',
                                    },
                                    '& .MuiTooltip-arrow': {
                                        color: '#304FFF',
                                    }
                                }
                            }}
                        >
                            <div>
                                <DatePicker
                                    value={newRowData.documentDate}
                                    onChange={(date) => handleNewRowChange('documentDate', date)}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            placeholder: `${t('ValueDate')} *`,
                                            onFocus: () => setFocusedField('documentDate'),
                                            onBlur: () => setFocusedField(null),
                                            sx: {
                                                '& .MuiInputBase-root': {
                                                    height: '40px',
                                                    fontSize: '0.875rem',
                                                    backgroundColor: '#fff',
                                                    '&:hover': {
                                                        backgroundColor: '#f8f8f8',
                                                    },
                                                    '&.Mui-focused': {
                                                        backgroundColor: '#fff',
                                                        boxShadow: '0 0 0 2px rgba(48, 79, 255, 0.2)',
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </Tooltip>
                    );
                }
                return formatDateNew(params.value) || '-';
            },
        },
        {
            field: 'amount',
            headerName: t('Amount'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            type: 'number',
            editable: true,
            flex: 1,
            viewTier: 'priority',
            filterOperators: numericFilterOperators,
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    const hasError = !newRowData.amount;
                    return (
                        <Tooltip
                            title={newRowData.amount || `${t('Amount')} (${t('Required')})`}
                            open={focusedField === 'amount'}
                            placement="top"
                            arrow
                            PopperProps={{
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        backgroundColor: hasError ? '#d32f2f' : '#4CAF50',
                                        fontSize: '1.1rem',
                                        padding: '12px 18px',
                                        maxWidth: '400px',
                                        fontWeight: 'bold',
                                    },
                                    '& .MuiTooltip-arrow': {
                                        color: hasError ? '#d32f2f' : '#4CAF50',
                                    }
                                }
                            }}
                        >
                            <TextField
                                size="small"
                                type="number"
                                value={newRowData.amount || ''}
                                onChange={(e) => handleNewRowChange('amount', e.target.value)}
                                onFocus={() => setFocusedField('amount')}
                                onBlur={() => setFocusedField(null)}
                                placeholder={`${t('Amount')} *`}
                                fullWidth
                                required
                                error={hasError}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: '40px',
                                        fontSize: '0.875rem',
                                        color: hasError ? '#d32f2f' : '#2e7d32',
                                        fontWeight: 'bold',
                                        backgroundColor: hasError ? '#fff5f5' : '#fff',
                                        border: hasError ? '1px solid #d32f2f' : '1px solid transparent',
                                        '&:hover': {
                                            backgroundColor: hasError ? '#fff0f0' : '#f0f8f0',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: '#fff',
                                            boxShadow: hasError
                                                ? '0 0 0 2px rgba(211, 47, 47, 0.2)'
                                                : '0 0 0 2px rgba(76, 175, 80, 0.2)',
                                        }
                                    }
                                }}
                            />
                        </Tooltip>
                    );
                }
                return (
                    <p
                        style={{
                            color: 'green',
                            fontSize: '16px',
                            fontWeight: 'bold',
                        }}
                    >
                        {params.value !== null ? beautifyNumber(params.value) : '0.00'}
                    </p>
                );
            },
        },
        {
            field: 'vat',
            headerName: t('VAT'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            type: 'number',
            editable: true,
            flex: 1,
            viewTier: 'full',
            filterOperators: numericFilterOperators,
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    const vatOptions = {
                        0: '0%',
                        16: `${t('AccountingTax')} (16%)`,
                        25: '25%',
                        66: '66%',
                        100: '100%'
                    };
                    const displayValue = vatOptions[newRowData.vat] || t('VAT');

                    return (
                        <Tooltip
                            title={displayValue}
                            open={focusedField === 'vat'}
                            placement="top"
                            arrow
                            PopperProps={{
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        backgroundColor: '#304FFF',
                                        fontSize: '1rem',
                                        padding: '10px 16px',
                                        maxWidth: '400px',
                                    },
                                    '& .MuiTooltip-arrow': {
                                        color: '#304FFF',
                                    }
                                }
                            }}
                        >
                            <div>
                                <Select
                                    size="small"
                                    value={newRowData.vat}
                                    onChange={(e) => handleNewRowChange('vat', parseInt(e.target.value))}
                                    onFocus={() => setFocusedField('vat')}
                                    onBlur={() => setFocusedField(null)}
                                    fullWidth
                                    sx={{
                                        height: '40px',
                                        fontSize: '0.875rem',
                                        '& .MuiSelect-select': {
                                            padding: '8px 12px',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'rgba(48, 79, 255, 0.04)',
                                        }
                                    }}
                                >
                                    <MenuItem value={0} sx={{ fontSize: '0.875rem' }}>0%</MenuItem>
                                    <MenuItem value={16} sx={{ fontSize: '0.875rem' }}>{t('AccountingTax')} (16%)</MenuItem>
                                    <MenuItem value={25} sx={{ fontSize: '0.875rem' }}>25%</MenuItem>
                                    <MenuItem value={66} sx={{ fontSize: '0.875rem' }}>66%</MenuItem>
                                    <MenuItem value={100} sx={{ fontSize: '0.875rem' }}>100%</MenuItem>
                                </Select>
                            </div>
                        </Tooltip>
                    );
                }
                return `${params.value}%`;
            },
        },
        // VATAmount Column
        {
            field: 'vatAmount',
            headerName: t('VATAmount'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            type: 'number',
            flex: 1,
            viewTier: 'standard',
            filterOperators: numericFilterOperators,
            valueFormatter: (params) => beautifyNumber(params || 0),
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    return beautifyNumber(newRowData.vatAmount || 0);
                }
                return beautifyNumber(params.value || 0);
            },
        },
        // NetAmount Column
        {
            field: 'netAmount',
            headerName: t('NetAmount'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            type: 'number',
            flex: 1,
            viewTier: 'compact',
            filterOperators: numericFilterOperators,
            valueFormatter: (params) => beautifyNumber(params || 0),
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    return beautifyNumber(newRowData.netAmount || 0);
                }
                return beautifyNumber(params.value || 0);
            },
        },
        {
            field: 'creditAccount',
            headerName: t('Credit Account'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            editable: false,
            viewTier: 'compact',
            filterOperators: stringFilterOperators,
            valueGetter: (value, row) => {
                if (row?.id === 'NEW_ROW') return '';
                const option = creditAccountOptions?.find((opt) => opt.cardId === value);
                return option ? `${option.cardId} - ${option.name}` : (value != null ? String(value) : '');
            },
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') return null;
                const rawValue = params.row.creditAccount;
                const option = creditAccountOptions.find((opt) => opt.cardId === rawValue);
                return (
                    <span>{option ? `${option.cardId} - ${option.name}` : (rawValue != null && rawValue !== '' ? String(rawValue) : '')}</span>
                );
            },
        },
        {
            field: 'debitAccount',
            headerName: t('Debit Account'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            editable: false,
            viewTier: 'compact',
            filterOperators: stringFilterOperators,
            valueGetter: (value, row) => {
                if (row?.id === 'NEW_ROW') return '';
                const option = debitAccountOptions?.find((opt) => opt.cardId === value);
                return option ? `${option.cardId} - ${option.name}` : (value != null ? String(value) : '');
            },
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') return null;
                const rawValue = params.row.debitAccount;
                const option = debitAccountOptions.find((opt) => opt.cardId === rawValue);
                return (
                    <span>{option ? `${option.cardId} - ${option.name}` : (rawValue != null && rawValue !== '' ? String(rawValue) : '')}</span>
                );
            },
        },
        {
            field: 'sortCodeId',
            headerName: t('Code ID'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'compact',
            filterOperators: stringFilterOperators,
            valueGetter: (value, row) => {
                if (row?.id === 'NEW_ROW') return '';
                const option = sortCodeIdOptions?.find((opt) => opt.sortCodeId === value);
                return option ? `${option.sortCodeId} - ${option.sortCodeName}` : (value != null ? String(value) : '');
            },
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') return null;
                const rawValue = params.row.sortCodeId ?? params.value ?? '';
                const option = sortCodeIdOptions.find((opt) => opt.sortCodeId === rawValue || String(opt.sortCodeId) === String(rawValue));
                const name = option ? option.sortCodeName : '';
                const code = rawValue != null && rawValue !== '' ? String(rawValue) : '';
                return (
                    <span>
                        {code} {name && `- ${name}`}
                    </span>
                );
            },
        },
        {
            field: 'uploadNumber',
            headerName: t('UploadNumber'),
            headerAlign: getAlignment(),
            align: getAlignment(),
            flex: 1,
            viewTier: 'full',
            filterOperators: stringFilterOperators,
            renderCell: (params) => {
                if (params.row.id === 'NEW_ROW') {
                    return <span>-</span>;
                }
                return <span>{params.value !== undefined ? params.value : 'N/A'}</span>;
            },
        },
        // Actions Column
        // {
        //     field: 'actions',
        //     type: 'actions',
        //     headerName: t('Actions'),
        //     headerAlign: getAlignment(),
        //     align: getAlignment(),
        //     flex: 1,
        //     getActions: (params) => {
        //         const row = params?.row || {};
        //         const rowIndex = data.findIndex((r) => r.id === row.id); // Find the index in the original data array

        //         return [
        //             <GridActionsCellItem
        //                 icon={<img src={editIcon} alt="Edit" />}
        //                 label="Edit"
        //                 onClick={() => handleEdit(row,rowIndex)}
        //                 showInMenu={false} // Optional
        //             />,
        //             (isRange ? !row.isClosedReportingMonth : !isClosedReportingMonth) && (
        //                 <GridActionsCellItem
        //                     icon={<img src={deleteIcon} alt="Delete" />}
        //                     label="Delete"
        //                     onClick={() => handleDeleteClick(row.id)}
        //                     showInMenu={false} // Optional
        //                 />
        //             ),
        //         ].filter(Boolean); // Filter out falsey values in case the condition is false
        //     },
        // },
    ].reverse();

    const visibleColumns = useMemo(() => {
        const included = VIEW_INCLUDES_TIER[tableView] || VIEW_INCLUDES_TIER.standard;
        return columns
            .filter((col) => included.includes(col.viewTier))
            .map(({ viewTier, ...col }) => col);
    }, [columns, tableView]);

    const {
        containerRef: fitContainerRef,
        columnsWithWidths,
        fitEnabled,
        requestFit,
        handleColumnWidthChange,
    } = useFitTableColumns({
        storageKey: 'journalEntryDataGrid',
        scope: tableView,
        columns: visibleColumns,
        visibilityModel: columnVisibilityModel,
        widths: columnWidths[tableView] || {},
        hasCheckbox: true,
        apiRef,
        onPersist: (flat) => {
            setColumnWidths((prev) => {
                const next = { ...prev, [tableView]: flat };
                try {
                    localStorage.setItem(STORAGE_KEY_JOURNAL_ENTRY_COLUMN_WIDTHS, JSON.stringify(next));
                } catch (_) {}
                return next;
            });
        },
    });

    const selectionCount = selectedRows.length;

    return (
        <div
            className={`${direction}`}
            style={{
                width: '100%',
                height: 'calc(100vh - 400px)',
                minHeight: 480,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                className={`${styles.toolbar} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                dir={direction}
                sx={{ flexShrink: 0 }}
            >
                {!isDepositTable && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            if (!isInlineAddMode) {
                                setIsInlineAddMode(true);
                                setAddNewJournalEntry(true);
                            }
                        }}
                        disabled={isInlineAddMode}
                        tabIndex={0}
                        sx={{
                            minHeight: 'var(--xtram-control-height, 40px)',
                            height: 'var(--xtram-control-height, 40px)',
                            backgroundColor: '#4CAF50',
                            color: '#fff',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 'var(--xtram-text-sm, 13px)',
                            borderRadius: '10px',
                            boxShadow: 'none',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                                backgroundColor: '#45a049',
                                boxShadow: 'none',
                            },
                            '&:disabled': {
                                backgroundColor: '#e0e0e0',
                                color: '#9e9e9e',
                            },
                            '&:focus': {
                                outline: '2px solid #304FFF',
                                outlineOffset: '2px',
                            }
                        }}
                    >
                        {t('AddInlineEntry') || 'Add Entry in Table'}
                    </Button>
                )}
                <div className={styles.tableViewGroup}>
                    <FormControl size="small" className={styles.tableViewControl}>
                        <InputLabel id="journal-entry-view-label">{t('TableView')}</InputLabel>
                        <Select
                            labelId="journal-entry-view-label"
                            value={tableView}
                            label={t('TableView')}
                            dir={direction}
                            MenuProps={TABLE_VIEW_MENU_PROPS}
                            onChange={(e) => {
                                const newView = e.target.value;
                                if (newView !== 'custom') {
                                    try {
                                        localStorage.setItem(STORAGE_KEY_JOURNAL_ENTRY_COLUMN_VISIBILITY_CUSTOM, JSON.stringify(columnVisibilityModel));
                                    } catch (_) {}
                                    setColumnVisibilityModel({ actions: true });
                                } else {
                                    try {
                                        const raw = localStorage.getItem(STORAGE_KEY_JOURNAL_ENTRY_COLUMN_VISIBILITY_CUSTOM);
                                        if (raw) {
                                            const parsed = JSON.parse(raw);
                                            if (typeof parsed === 'object' && parsed !== null) setColumnVisibilityModel({ actions: true, ...parsed });
                                        }
                                    } catch (_) {}
                                }
                                setTableView(newView);
                            }}
                            sx={{
                                height: 'var(--xtram-control-height, 40px)',
                                minHeight: 'var(--xtram-control-height, 40px)',
                                '& .MuiSelect-select': {
                                    textAlign: direction === 'rtl' ? 'left' : 'right',
                                    py: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                            }}
                        >
                            <MenuItem dir={direction} value="compact">{t('TableViewCompact')}</MenuItem>
                            <MenuItem dir={direction} value="standard">{t('TableViewStandard')}</MenuItem>
                            <MenuItem dir={direction} value="full">{t('TableViewFull')}</MenuItem>
                            <MenuItem dir={direction} value="custom">{t('TableViewCustom')}</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ViewColumnIcon />}
                        onClick={(e) => setColumnsPanelAnchor(e.currentTarget)}
                        className={styles.columnsBtn}
                        sx={{
                            minHeight: 'var(--xtram-control-height, 40px)',
                            height: 'var(--xtram-control-height, 40px)',
                            textTransform: 'none',
                            borderRadius: '10px',
                            borderColor: '#e5e7f6',
                            color: '#304FFF',
                            fontSize: 'var(--xtram-table-view-label, 11px)',
                            fontWeight: 600,
                            background: '#f5f6fd',
                            '&:hover': { borderColor: '#304FFF', background: '#eaedff' },
                        }}
                    >
                        {t('ShowHideColumns')}
                    </Button>
                    <FitColumnsButton active={fitEnabled} onClick={requestFit} />
                </div>
                <label className={`${styles.byProjects}${byProjectsFilterActive ? ` ${styles.checked}` : ''}`}>
                    <Checkbox
                        checked={!!byProjectsFilterActive}
                        onChange={(event) => onByProjectsFilterToggle?.(event.target.checked)}
                        name="ByProjects"
                        size="small"
                        sx={{
                            padding: '2px',
                            color: '#c5c8e0',
                            '&.Mui-checked': { color: '#304fff' },
                        }}
                    />
                    <span>{t('ByProjects')}</span>
                </label>
                {byProjectsFilterActive && (
                    <div className={styles.projectPickers} dir={direction}>
                        {loadingSegmentations && <CircularProgress size={22} />}
                        <div className={styles.projectField}>
                            <Autocomplete
                                size="small"
                                loading={loadingSegmentations}
                                options={projectAutocompleteOptions}
                                getOptionLabel={(opt) => opt?.label ?? ''}
                                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                                value={selectedToSegmentation}
                                onChange={onToSegmentationChange}
                                PopperComponent={(popperProps) => (
                                    <StyledProjectsPopper {...popperProps} dir={direction} />
                                )}
                                ListboxProps={{ dir: direction }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('ToProject')}
                                        size="small"
                                        variant="standard"
                                        dir={direction}
                                        InputProps={{
                                            ...params.InputProps,
                                            disableUnderline: true,
                                        }}
                                    />
                                )}
                                clearOnBlur={false}
                                dir={direction}
                            />
                        </div>
                        <div className={styles.projectField}>
                            <Autocomplete
                                size="small"
                                loading={loadingSegmentations}
                                options={projectAutocompleteOptions}
                                getOptionLabel={(opt) => opt?.label ?? ''}
                                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                                value={selectedFromSegmentation}
                                onChange={onFromSegmentationChange}
                                PopperComponent={(popperProps) => (
                                    <StyledProjectsPopper {...popperProps} dir={direction} />
                                )}
                                ListboxProps={{ dir: direction }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('FromProject')}
                                        size="small"
                                        variant="standard"
                                        dir={direction}
                                        InputProps={{
                                            ...params.InputProps,
                                            disableUnderline: true,
                                        }}
                                    />
                                )}
                                clearOnBlur={false}
                                dir={direction}
                            />
                        </div>
                    </div>
                )}
                <Popover
                    open={Boolean(columnsPanelAnchor)}
                    anchorEl={columnsPanelAnchor}
                    onClose={() => setColumnsPanelAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'rtl' ? 'right' : 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: direction === 'rtl' ? 'right' : 'left' }}
                    PaperProps={{ sx: COLUMN_PANEL_PAPER_SX }}
                >
                    {visibleColumns.map((col, index) => {
                        const isChecked = columnVisibilityModel[col.field] !== false;
                        return (
                            <FormControlLabel
                                key={col.field}
                                dir={direction}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={isChecked}
                                        onChange={(e) => {
                                            setColumnVisibilityModel((prev) => ({
                                                ...prev,
                                                [col.field]: e.target.checked,
                                            }));
                                            setTableView((prev) => (prev === 'custom' ? prev : 'custom'));
                                        }}
                                        sx={{
                                            color: isChecked ? '#304FFF' : '#8b93a7',
                                            '&.Mui-checked': { color: '#304FFF' },
                                        }}
                                    />
                                }
                                label={col.headerName || col.field}
                                sx={{
                                    display: 'flex',
                                    width: '100%',
                                    mx: 0,
                                    py: 0.65,
                                    minHeight: 'var(--xtram-menu-item-height)',
                                    height: 'auto',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s ease, color 0.2s ease',
                                    borderBottom: index < visibleColumns.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    backgroundColor: isChecked ? 'rgba(48, 79, 255, 0.1)' : 'transparent',
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: 'var(--xtram-text-sm)',
                                        fontWeight: isChecked ? 500 : 400,
                                        lineHeight: 1.35,
                                        color: isChecked ? '#304FFF' : '#696665',
                                    },
                                    '&:hover': {
                                        backgroundColor: '#304fff',
                                        '& .MuiFormControlLabel-label': { color: '#ffffff' },
                                    },
                                    '&:hover .MuiCheckbox-root': { color: '#ffffff' },
                                    '&:hover .MuiCheckbox-root.Mui-checked': { color: '#ffffff' },
                                }}
                            />
                        );
                    })}
                    <Box sx={{ px: 1.5, py: 0.75, display: 'flex', gap: 1, borderTop: '1px solid #f0f0f0' }}>
                        <Button
                            size="small"
                            onClick={() => {
                                const allVisible = {};
                                visibleColumns.forEach((c) => { allVisible[c.field] = true; });
                                setColumnVisibilityModel((prev) => ({ ...prev, ...allVisible }));
                            }}
                            sx={{ textTransform: 'none', fontSize: 'var(--xtram-text-xs, 11px)', color: '#304FFF' }}
                        >
                            {t('ShowAll')}
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                const allHidden = {};
                                visibleColumns.forEach((c) => { allHidden[c.field] = false; });
                                setColumnVisibilityModel((prev) => ({ ...prev, ...allHidden }));
                            }}
                            sx={{ textTransform: 'none', fontSize: 'var(--xtram-text-xs, 11px)', color: '#696665' }}
                        >
                            {t('HideAll')}
                        </Button>
                    </Box>
                </Popover>
                {selectionCount > 0 && (
                    <Box className={styles.selectionBar}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '1A1F49',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                            }}
                        >
                            {t('Selected')}:
                        </Typography>
                        <Chip
                            label={selectionCount}
                            size="small"
                            sx={{
                                backgroundColor: '#304FFF',
                                color: '#ffffff',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                height: '22px',
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#434343',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                            }}
                        >
                            {t('NetAmount')}:
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '1A1F49',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                fontFamily: 'monospace',
                            }}
                        >
                            {beautifyNumber(selectedTotals.netAmount)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#434343',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                            }}
                        >
                            {t('VATAmount')}:
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#434343',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                fontFamily: 'monospace',
                            }}
                        >
                            {beautifyNumber(selectedTotals.vatAmount)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: '1A1F49',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                            }}
                        >
                            {t('Amount')}:
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '1A1F49',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                fontFamily: 'monospace',
                            }}
                        >
                            {beautifyNumber(selectedTotals.amount)}
                        </Typography>
                    </Box>
                    </Box>
                )}
            </Box>
            <AddJournalEntryModal
                open={isInlineAddMode}
                onClose={handleCancelNewRow}
                onSave={handleSaveNewRow}
                t={t}
                direction={direction}
                adapterLocale={getLocale()}
                sortCodeIdOptions={sortCodeIdOptions}
                enhancedDebitAccountOptions={enhancedDebitAccountOptions}
                enhancedCreditAccountOptions={enhancedCreditAccountOptions}
                handleNoOptionCreditDebitClick={handleNoOptionCreditDebitClick}
                searchTermDebit={searchTermDebit}
                setSearchTermDebit={setSearchTermDebit}
                searchTermCredit={searchTermCredit}
                setSearchTermCredit={setSearchTermCredit}
                sortCodeInputRef={sortCodeInputRef}
                applyAccountCardToDraftRef={applyAccountCardToDraftRef}
            />
            <AddAccountCardModal
                accountCardOptions={accountCardOptions || []}
                open={editedAddAccountCard}
                onClose={handleCloseAddAccountCard}
                editedItem={newAccountCard}
                isEdit={isEditAccountCard}
                isRange={isRange}
                isClosedReportingMonth={isClosedReportingMonth}
                palCodes={palCodes}
                propPalCode={palCode}
                setPalCode={setPalCode}
                direction={direction}
                isAdd={true}
                client={client || {}}
                token={token}
                setLoading={setLoading}
                handleChangeAfterAdd={handleChangeAfterAdd}
                fetchEnums={fetchEnums}
                type={addAccountCardType}
            />
            <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={getLocale()}
            >
                <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
                    {showSearch && (
                        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8, flexShrink: 0 }}>
                            <TextField
                                size="small"
                                dir={direction}
                                variant="outlined"
                                placeholder={t('Search')}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                sx={{
                                    width: 280,
                                    '& .MuiOutlinedInput-root': {
                                        height: 40,
                                        borderRadius: '12px',
                                        backgroundColor: '#f5f6fd',
                                        '& fieldset': { borderColor: '#e5e7f6', borderWidth: 2 },
                                        '&:hover fieldset': { borderColor: '#c9d0ff' },
                                        '&.Mui-focused fieldset': { borderColor: '#304FFF' },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: '#304FFF', fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                            />
                        </div>
                    )}
                    <Box ref={fitContainerRef} sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                        <StyledDataGrid
                            key={tableView}
                            apiRef={apiRef}
                    rows={filteredRows}
                            columns={columnsWithWidths}
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                            onColumnWidthChange={handleColumnWidthChange}
                            checkboxSelection
                            disableRowSelectionOnClick
                            columnHeaderHeight={42}
                            getRowHeight={() => 'auto'}
                            getEstimatedRowHeight={() => 72}
                            rowSelectionModel={selectedRows}
                            onRowSelectionModelChange={(ids) => handleSelectionModelChange(ids)}
                            getRowId={(row) => row.id}
                            getRowClassName={getRowClassName}
                            components={{
                                Toolbar: GridToolbar,
                            }}
                            pagination
                            sortingOrder={['asc', 'desc']}
                            isRowSelectable={isRowSelectable}
                            onCellDoubleClick={handleCellDoubleClick}
                            editMode="row"
                            rowModesModel={rowModesModel}
                            sx={{ height: '100%' }}
                            onFilterModelChange={() => {
                        requestAnimationFrame(emitFilteredRows);
                    }}
                            onSortModelChange={() => {
                        requestAnimationFrame(emitFilteredRows);
                    }}
                />
                    </Box>
                </Box>
            </LocalizationProvider>
        </div>
    );
});

export default JournalEntryDataGrid;
